import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PersonIcon from '@mui/icons-material/Person';
import ViewListIcon from '@mui/icons-material/ViewList';
import { Box, BottomNavigation, BottomNavigationAction, Paper } from '@mui/material';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAnnouncerEngine } from '../hooks/useAnnouncer';
import { useMarkets } from '../hooks/useMarkets';
import { useAuthStore } from '../stores/authStore';
import { ConnectionBar } from './ConnectionBar';
import { TopBar } from './TopBar';
import { colors, MIN_TAP_TARGET } from '../themes/theme';

interface AppShellProps {
    children: ReactNode;
}

interface TabDef {
    label: string;
    value: string;
    icon: React.ReactNode;
}

const BASE_TABS: TabDef[] = [
    { label: 'Board', value: '/', icon: <ViewListIcon /> },
    { label: 'New bet', value: '/new', icon: <AddCircleOutlineIcon /> },
    { label: 'Standings', value: '/standings', icon: <EmojiEventsIcon /> },
    { label: 'Me', value: '/me', icon: <PersonIcon /> },
];

const ADMIN_TAB: TabDef = {
    label: 'Admin',
    value: '/admin',
    icon: <AdminPanelSettingsIcon />,
};

export function AppShell({ children }: AppShellProps) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isAdmin = useAuthStore((s) => s.session?.isAdmin ?? false);

    // Mounted here so announcements continue whatever screen is open. Shares
    // the board's query key, so it adds no polling of its own.
    useAnnouncerEngine(useMarkets().data);

    const tabs = isAdmin ? [...BASE_TABS, ADMIN_TAB] : BASE_TABS;

    // Nested routes still light up their parent tab.
    const active =
        tabs
            .map((tab) => tab.value)
            .filter((value) => value !== '/' && pathname.startsWith(value))
            .at(0) ?? '/';

    return (
        <Box
            sx={{
                // dvh, not vh: iOS Safari's collapsing toolbar makes vh overflow.
                minHeight: '100dvh',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: colors.ground,
            }}
        >
            <Box
                component="main"
                sx={{
                    flex: 1,
                    px: 2,
                    // The top bar handles the notch inset itself, being the
                    // thing that sits against it.
                    pt: 0,
                    // Clears the fixed tab bar plus the home indicator.
                    pb: `calc(${MIN_TAP_TARGET + 30}px + env(safe-area-inset-bottom))`,
                    maxWidth: 560,
                    width: '100%',
                    mx: 'auto',
                    // Landscape and the notch also eat the sides.
                    pl: 'max(16px, env(safe-area-inset-left))',
                    pr: 'max(16px, env(safe-area-inset-right))',
                }}
            >
                <TopBar />
                <Box sx={{ pt: 2 }}>
                    <ConnectionBar />
                    {children}
                </Box>
            </Box>

            <Paper
                component="nav"
                elevation={0}
                sx={{
                    position: 'fixed',
                    // Anchored to the true bottom of the viewport. On iOS the
                    // area under the home indicator belongs to this bar, so its
                    // own background fills it rather than the page showing
                    // through as a strip.
                    bottom: 0,
                    left: 0,
                    right: 0,
                    borderLeft: 0,
                    borderRight: 0,
                    borderBottom: 0,
                    borderRadius: 0,
                    bgcolor: colors.surface,
                    pb: 'env(safe-area-inset-bottom)',
                    zIndex: 10,
                }}
            >
                <BottomNavigation
                    value={active}
                    onChange={(_event, value: string) => navigate(value)}
                    showLabels
                    sx={{ bgcolor: 'transparent', maxWidth: 560, mx: 'auto' }}
                >
                    {tabs.map((tab) => (
                        <BottomNavigationAction
                            key={tab.value}
                            label={tab.label}
                            value={tab.value}
                            icon={tab.icon}
                            // Five tabs need to survive a narrow phone.
                            sx={{ minWidth: 0, px: 0.5 }}
                        />
                    ))}
                </BottomNavigation>
            </Paper>
        </Box>
    );
}
